import { useEffect, useState, useCallback } from "react";
import { api, formatTL } from "@/lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, CheckCircle } from "lucide-react";

const STATUSES = {
  payment_pending: "Ödeme Bekliyor", payment_success: "Ödeme Başarılı", order_created: "Oluşturuldu",
  processing: "İşlemde", completed: "Tamamlandı", partial: "Kısmi", cancelled: "İptal", error: "Hata",
};
const COLORS = {
  payment_pending: "text-yellow-400 bg-yellow-500/15", payment_success: "text-blue-400 bg-blue-500/15",
  order_created: "text-blue-400 bg-blue-500/15", processing: "text-indigo-400 bg-indigo-500/15",
  completed: "text-green-400 bg-green-500/15", partial: "text-orange-400 bg-orange-500/15",
  cancelled: "text-red-400 bg-red-500/15", error: "text-red-400 bg-red-500/15",
};

export default function Orders() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ page, limit: 30 });
    if (status) qs.set("status", status);
    if (search) qs.set("search", search);
    api.get(`/admin/orders?${qs}`).then((r) => setData(r.data)).catch(() => {});
  }, [status, search, page]);
  useEffect(() => { load(); }, [load]);

  const confirm = async (no) => {
    try { await api.post(`/admin/orders/${no}/confirm-payment`); toast.success("Ödeme onaylandı, JungleSMM'e gönderildi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  const refresh = async (no) => {
    try { await api.post(`/admin/orders/${no}/refresh`); toast.success("Durum güncellendi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  return (
    <div>
      <h1 className="text-2xl font-extrabold text-white mb-6">Siparişler</h1>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 bg-[#171922] border border-white/10 rounded-xl px-4 flex-1 min-w-[200px]">
          <Search size={16} className="text-gray-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Sipariş no, e-posta..." className="flex-1 bg-transparent py-2.5 text-white text-sm outline-none" data-testid="orders-search" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm" data-testid="orders-status-filter">
          <option value="">Tüm Durumlar</option>
          {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-gray-400 text-left border-b border-white/10">
            {["Sipariş No", "Servis", "Hedef", "Miktar", "Tutar", "Durum", "İşlem"].map((h) => <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {data.items.map((o) => (
              <tr key={o.order_number} className="border-b border-white/5 hover:bg-white/5" data-testid={`order-row-${o.order_number}`}>
                <td className="px-4 py-3 text-white font-mono text-xs whitespace-nowrap">{o.order_number}</td>
                <td className="px-4 py-3 text-gray-300 max-w-[220px] truncate" title={o.service_name}>{o.service_name}</td>
                <td className="px-4 py-3 text-gray-400 max-w-[140px] truncate" title={o.link}>{o.link}</td>
                <td className="px-4 py-3 text-gray-300">{o.quantity}</td>
                <td className="px-4 py-3 text-white whitespace-nowrap">{formatTL(o.total)}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${COLORS[o.status] || ""}`}>{STATUSES[o.status] || o.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {o.status === "payment_pending" && <button onClick={() => confirm(o.order_number)} className="p-1.5 rounded-lg bg-green-500/15 text-green-400 hover:bg-green-500/25" title="Ödeme Onayla" data-testid={`confirm-${o.order_number}`}><CheckCircle size={15} /></button>}
                    {o.provider_order_id && <button onClick={() => refresh(o.order_number)} className="p-1.5 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10" title="Durum Yenile"><RefreshCw size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">Sipariş bulunamadı</td></tr>}
          </tbody>
        </table>
      </div>
      {data.pages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: Math.min(data.pages, 12) }).map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)} className={`w-9 h-9 rounded-lg text-sm font-semibold ${page === i + 1 ? "bg-white text-[#1f222d]" : "bg-white/5 text-gray-300"}`}>{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
}
